import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  feeCategoriesApi, 
  UpdateFeeCategoryData, 
  BuildingOption, 
  FeePricingType, 
  FeeQuantitySource,
  FeeTierData
} from '../../api/feeCategoriesApi';
import { useLang } from '../../contexts/LangContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import CompositePricingFields, { CompositePricingField, CompositePricingTextField } from '../../components/FeeCategories/CompositePricingFields';
import CompositeFeeGroupSettings from '../../components/FeeCategories/CompositeFeeGroupSettings';
import TierLevelPricingFields from '../../components/FeeCategories/TierLevelPricingFields';
import {
  calculateCompositePricing,
  applyCompositeFieldChange,
  applyVatPercentageChange,
  calculateCompositePricingDefaults,
  resolveCompositeAmountOverride,
  type CompositePricingDriverField,
} from '../../utils/feeCategoryCompositePricing';
import {
  buildTierSubmitPayload,
  extractTierCommonSettings,
  formatTierUsageToForDisplay,
  recalculateAllTiers,
  recalculateTierWithCommon,
  TierCommonField,
  TierLevelField,
} from '../../utils/tierCompositePricing';
import { getAccessCardKindForFeeCode } from '../../utils/accessCardFee';

const EditFeeCategory: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useLang();
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<UpdateFeeCategoryData>({
    name: '',
    feeCode: '',
    buildingId: '',
    pricingType: FeePricingType.SINGLE_TIER,
    basePrice: 0,
    vatPercentage: 0,
    isMonthlyFixed: false,
    autoCloseMonthly: false,
    billingTiming: 'PREPAID',
    isActive: true,
    notes: '',
    waiveFirstVehicleCardIssuanceFee: false,
    quantitySource: FeeQuantitySource.MANUAL,
    quantityUnit: '',
    quotaLimit: 0,
    quotaPrice: 0,
    overQuotaPrice1: 0,
    overQuotaPrice2: 0,
    wastagePercentage: 0,
    environmentalFee: 0,
    quotaCalculationType: 'STANDARD',
    baseQuotaPrice: 0,
    baseOverQuotaPrice1: 0,
    baseOverQuotaPrice2: 0,
    extraFee1Percent: 0,
    extraFee1VatPercent: 0,
    extraFee1Fixed: 0,
    extraFee1Name: '',
    extraFee2Percent: 0,
    extraFee2VatPercent: 0,
    extraFee2Fixed: 0,
    extraFee2Name: '',
    feeTiers: []
  });

  const [feeTiers, setFeeTiers] = useState<FeeTierData[]>([]);

  const handleCompositeFieldChange = (field: CompositePricingField, value: number | null) => {
    setFormData((prev) => applyCompositeFieldChange(prev, field as CompositePricingDriverField, value ?? undefined));
  };

  const handleCompositeTextChange = (field: CompositePricingTextField, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const clearTierAmountOverrides = (tier: FeeTierData): FeeTierData => ({
    ...tier,
    vatAmount: undefined,
    extraFee1Amount: undefined,
    extraFee1VatAmount: undefined,
    extraFee2Amount: undefined,
    extraFee2VatAmount: undefined,
    totalUnitPrice: undefined,
  });

  const handleTierCommonNumberChange = (field: TierCommonField, value: number | null) => {
    const nextForm = { ...formData, [field]: value ?? undefined };
    setFormData(nextForm);
    setFeeTiers((prev) =>
      recalculateAllTiers(
        prev.map(clearTierAmountOverrides),
        extractTierCommonSettings(nextForm),
        nextForm.vatPercentage || 0,
      ),
    );
  };

  const handleTierCommonTextChange = (field: CompositePricingTextField, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTierLevelPricingChange = (index: number, field: TierLevelField, value: number | null) => {
    setFeeTiers((prev) => {
      const updated = [...prev];
      const tier = { ...updated[index], [field]: value ?? undefined };
      if (field !== 'totalUnitPrice') {
        tier.totalUnitPrice = undefined;
      }
      updated[index] = recalculateTierWithCommon(
        tier,
        extractTierCommonSettings(formData),
        formData.vatPercentage || 0,
        field,
      );
      return updated;
    });
  };

  const handleTierVatPercentageChange = (vatPercentage: number) => {
    const nextForm = { ...formData, vatPercentage };
    setFormData(nextForm);
    setFeeTiers((prev) =>
      recalculateAllTiers(
        prev.map(clearTierAmountOverrides),
        extractTierCommonSettings(nextForm),
        vatPercentage,
      ),
    );
  };

  // Number formatting utility functions
  const formatNumberDisplay = (value: number | string) => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/\./g, '')) : value;
    if (isNaN(numValue)) return '';
    return new Intl.NumberFormat('vi-VN').format(numValue);
  };

  const parseNumberInput = (formattedValue: string) => {
    return formattedValue.replace(/\./g, '');
  };

  // Calculate effective prices (f1, f2, f3) from base prices
  const calculateEffectivePrice = (basePrice: number, vatPercentage: number, environmentalFee: number): number => {
    const result = basePrice * (1 + vatPercentage / 100 + environmentalFee / 100);
    // Round to 2 decimal places to avoid floating point precision issues
    return Math.round(result * 100) / 100;
  };

  // Calculate effective prices for display (when base prices change)
  const calculatedQuotaPrice = calculateEffectivePrice(formData.baseQuotaPrice || 0, formData.vatPercentage || 0, formData.environmentalFee || 0);
  const calculatedOverQuotaPrice1 = calculateEffectivePrice(formData.baseOverQuotaPrice1 || 0, formData.vatPercentage || 0, formData.environmentalFee || 0);
  const calculatedOverQuotaPrice2 = calculateEffectivePrice(formData.baseOverQuotaPrice2 || 0, formData.vatPercentage || 0, formData.environmentalFee || 0);

  useEffect(() => {
    loadBuildings();
    if (id) {
      loadFeeCategory();
    }
  }, [id]);

  const loadBuildings = async () => {
    try {
      const data = await feeCategoriesApi.getBuildingsForUser();
      setBuildings(data);
    } catch (err) {
      setError(getApiErrorMessage(err, t('failedToLoad')));
    }
  };

  const loadFeeCategory = async () => {
    if (!id) return;
    
    try {
      const data = await feeCategoriesApi.getById(id);
      const firstTier = data.feeTiers?.[0];
      const compositeDefaults = calculateCompositePricingDefaults({
        basePrice: data.basePrice,
        vatPercentage: data.vatPercentage,
        extraFee1Percent: data.extraFee1Percent ?? firstTier?.extraFee1Percent,
        extraFee1VatPercent: data.extraFee1VatPercent ?? firstTier?.extraFee1VatPercent,
        extraFee1Fixed: data.extraFee1Fixed ?? firstTier?.extraFee1Fixed,
        extraFee2Percent: data.extraFee2Percent ?? firstTier?.extraFee2Percent,
        extraFee2VatPercent: data.extraFee2VatPercent ?? firstTier?.extraFee2VatPercent,
        extraFee2Fixed: data.extraFee2Fixed ?? firstTier?.extraFee2Fixed,
      });
      setFormData({
        name: data.name,
        feeCode: data.feeCode || '',
        buildingId: data.buildingId,
        pricingType: data.pricingType,
        basePrice: data.basePrice,
        vatPercentage: data.vatPercentage,
        vatAmount: resolveCompositeAmountOverride(data.vatAmount, compositeDefaults.vatAmount),
        isMonthlyFixed: data.isMonthlyFixed,
        autoCloseMonthly: data.autoCloseMonthly,
        billingTiming: data.billingTiming || 'PREPAID',
        isActive: data.isActive,
        notes: data.notes,
        waiveFirstVehicleCardIssuanceFee: data.waiveFirstVehicleCardIssuanceFee ?? false,
        quantitySource: data.quantitySource || FeeQuantitySource.MANUAL,
        quantityUnit: data.quantityUnit || '',
        quotaLimit: data.quotaLimit || 0,
        quotaPrice: data.quotaPrice || 0,
        overQuotaPrice1: data.overQuotaPrice1 || 0,
        overQuotaPrice2: data.overQuotaPrice2 || 0,
        wastagePercentage: data.wastagePercentage || 0,
        environmentalFee: data.environmentalFee || 0,
        quotaCalculationType: data.quotaCalculationType || 'STANDARD',
        baseQuotaPrice: data.baseQuotaPrice || 0,
        baseOverQuotaPrice1: data.baseOverQuotaPrice1 || 0,
        baseOverQuotaPrice2: data.baseOverQuotaPrice2 || 0,
        extraFee1Percent: data.extraFee1Percent ?? firstTier?.extraFee1Percent ?? 0,
        extraFee1Amount: data.extraFee1Amount,
        extraFee1VatPercent: data.extraFee1VatPercent ?? firstTier?.extraFee1VatPercent ?? 0,
        extraFee1VatAmount: data.extraFee1VatAmount,
        extraFee1Fixed: data.extraFee1Fixed ?? firstTier?.extraFee1Fixed ?? 0,
        extraFee1Name: data.extraFee1Name || firstTier?.extraFee1Name || '',
        extraFee2Percent: data.extraFee2Percent ?? firstTier?.extraFee2Percent ?? 0,
        extraFee2Amount: data.extraFee2Amount,
        extraFee2VatPercent: data.extraFee2VatPercent ?? firstTier?.extraFee2VatPercent ?? 0,
        extraFee2VatAmount: data.extraFee2VatAmount,
        extraFee2Fixed: data.extraFee2Fixed ?? firstTier?.extraFee2Fixed ?? 0,
        extraFee2Name: data.extraFee2Name || firstTier?.extraFee2Name || '',
        totalUnitPrice: data.totalUnitPrice,
      });

      if (data.feeTiers && data.feeTiers.length > 0) {
        setFeeTiers(data.feeTiers.map((tier, index, arr) => ({
          tierLevel: tier.tierLevel,
          usageFrom: tier.usageFrom,
          usageTo: formatTierUsageToForDisplay(tier.usageTo, {
            isLastTier: index === arr.length - 1,
          }),
          price: tier.price,
          basePrice: tier.basePrice,
          vatAmount: tier.vatAmount,
          unit: tier.unit,
          extraFee1Amount: tier.extraFee1Amount,
          extraFee1VatAmount: tier.extraFee1VatAmount,
          extraFee2Amount: tier.extraFee2Amount,
          extraFee2VatAmount: tier.extraFee2VatAmount,
        })));
      } else {
        setFeeTiers([{ tierLevel: 1, usageFrom: 0, usageTo: '', price: '', unit: '' }]);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t('failedToLoadFeeCategory')));
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (field: keyof UpdateFeeCategoryData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleChange = (field: keyof UpdateFeeCategoryData) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePricingTypeChange = (type: FeePricingType) => {
    setFormData(prev => ({ 
      ...prev, 
      pricingType: type,
      basePrice: type === FeePricingType.SINGLE_TIER ? prev.basePrice : undefined,
      // Reset quota-based fields when switching away from QUOTA_BASED
      quotaLimit: type === FeePricingType.QUOTA_BASED ? prev.quotaLimit : 0,
      quotaPrice: type === FeePricingType.QUOTA_BASED ? prev.quotaPrice : 0,
      overQuotaPrice1: type === FeePricingType.QUOTA_BASED ? prev.overQuotaPrice1 : 0,
      overQuotaPrice2: type === FeePricingType.QUOTA_BASED ? prev.overQuotaPrice2 : 0,
      wastagePercentage: type === FeePricingType.QUOTA_BASED ? prev.wastagePercentage : 0,
      environmentalFee: type === FeePricingType.QUOTA_BASED ? prev.environmentalFee : 0,
      quotaCalculationType: type === FeePricingType.QUOTA_BASED ? prev.quotaCalculationType : 'STANDARD',
      baseQuotaPrice: type === FeePricingType.QUOTA_BASED ? prev.baseQuotaPrice : 0,
      baseOverQuotaPrice1: type === FeePricingType.QUOTA_BASED ? prev.baseOverQuotaPrice1 : 0,
      baseOverQuotaPrice2: type === FeePricingType.QUOTA_BASED ? prev.baseOverQuotaPrice2 : 0
    }));
  };

  const handleTierChange = (index: number, field: keyof FeeTierData, value: any) => {
    const updatedTiers = [...feeTiers];
    updatedTiers[index] = { ...updatedTiers[index], [field]: value };
    
    // Set usageFrom automatically based on previous tier's usageTo only when usageTo changes
    if (field === 'usageTo') {
      updatedTiers.forEach((tier, i) => {
        if (i === 0) {
          tier.usageFrom = 0;
        } else {
          tier.usageFrom = updatedTiers[i - 1]?.usageTo || 0;
        }
      });
    }
    
    setFeeTiers(updatedTiers);
  };

  const addTier = () => {
    const newTierLevel = feeTiers.length + 1;
    const previousTierUsageTo = feeTiers[feeTiers.length - 1]?.usageTo || '';
    setFeeTiers(prev => [...prev, { 
      tierLevel: newTierLevel, 
      usageFrom: previousTierUsageTo, 
      usageTo: '', 
      price: '', 
      unit: '',
    }]);
  };

  const removeTier = (index: number) => {
    if (feeTiers.length > 1) {
      const updatedTiers = feeTiers.filter((_, i) => i !== index);
      // Reorder tier levels
      const reorderedTiers = updatedTiers.map((tier, i) => ({ ...tier, tierLevel: i + 1 }));
      setFeeTiers(reorderedTiers);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.buildingId) {
      setError(t('pleaseSelectBuilding'));
      return;
    }

    if (!formData.name?.trim()) {
      setError(t('pleaseEnterServiceName'));
      return;
    }

    const feeCode = formData.feeCode?.trim().toUpperCase();
    if (feeCode && !/^[A-Z0-9-]+$/.test(feeCode)) {
      setError(t('invalidFeeCode'));
      return;
    }

    if (!id) {
      setError('Fee category ID not found');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Convert string values to numbers for feeTiers
      const processedFeeTiers = formData.pricingType === FeePricingType.TIERED 
        ? feeTiers.map((tier, index) =>
            buildTierSubmitPayload(
              tier,
              extractTierCommonSettings(formData),
              formData.vatPercentage || 0,
              { isLastTier: index === feeTiers.length - 1 },
            ),
          )
        : undefined;

      const singleTierBreakdown = formData.pricingType === FeePricingType.SINGLE_TIER
        ? calculateCompositePricing({
            ...formData,
            vatPercentage: formData.vatPercentage,
          })
        : null;

      const { buildingId: _buildingId, ...formFields } = formData;
      const submitData: UpdateFeeCategoryData = {
        ...formFields,
        feeCode: feeCode || null,
        vatAmount: singleTierBreakdown
          ? (formData.vatAmount ?? singleTierBreakdown.vatAmount)
          : formData.vatAmount,
        totalUnitPrice: singleTierBreakdown
          ? (formData.totalUnitPrice ?? singleTierBreakdown.totalUnitPrice)
          : formData.totalUnitPrice,
        feeTiers: processedFeeTiers,
        // Use the f1, f2, f3 values from form (user-editable)
        quotaPrice: formData.pricingType === FeePricingType.QUOTA_BASED ? (formData.quotaPrice || calculatedQuotaPrice) : formData.quotaPrice,
        overQuotaPrice1: formData.pricingType === FeePricingType.QUOTA_BASED ? (formData.overQuotaPrice1 || calculatedOverQuotaPrice1) : formData.overQuotaPrice1,
        overQuotaPrice2: formData.pricingType === FeePricingType.QUOTA_BASED ? (formData.overQuotaPrice2 || calculatedOverQuotaPrice2) : formData.overQuotaPrice2,
      };

      await feeCategoriesApi.update(id, submitData);
      navigate('/fee-categories');
    } catch (err) {
      setError(getApiErrorMessage(err, t('errorUpdatingFeeCategory')));
    } finally {
      setLoading(false);
    }
  };

  const selectedBuilding = buildings.find(b => b.id === formData.buildingId);
  const isVehicleCardIssuanceFee = getAccessCardKindForFeeCode(formData.feeCode) === 'VEHICLE';

  if (loadingData) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="text-gray-500">{t('loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('editFeeServiceTitle')}</h1>
        <button
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-800 px-4 py-2 border border-gray-300 rounded-lg transition-colors"
        >
          {t('backButton')}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Building Selection */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="font-medium text-gray-800 mb-4">{t('selectBuildingSection')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('building')} <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.buildingId}
                onChange={(e) => handleInputChange('buildingId', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">{t('selectBuildingPlaceholder')}</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
            {selectedBuilding && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('manager')}
                </label>
                <div className="text-gray-600 py-2">
                  {selectedBuilding.operator.name} ({selectedBuilding.operator.email})
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Service Fee Information */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="font-medium text-gray-800 mb-4">{t('serviceInformation')}</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('feeCode')}
              </label>
              <input
                type="text"
                value={formData.feeCode || ''}
                onChange={(e) => handleInputChange('feeCode', e.target.value.toUpperCase())}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                placeholder={t('enterFeeCode')}
              />
              <p className="mt-1 text-xs text-gray-500">{t('feeCodeHint')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('serviceNameLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('serviceNamePlaceholder')}
                required
              />
            </div>

            {/* Pricing Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loại tính phí
              </label>
              <select
                value={formData.pricingType}
                onChange={(e) => handlePricingTypeChange(e.target.value as FeePricingType)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={FeePricingType.SINGLE_TIER}>Đơn giá cố định</option>
                <option value={FeePricingType.TIERED}>Đơn giá bậc thang</option>
                <option value={FeePricingType.QUOTA_BASED}>Tính theo định mức</option>
              </select>
            </div>

            {/* Single Tier Pricing */}
            {formData.pricingType === FeePricingType.SINGLE_TIER && (
              <CompositePricingFields
                values={formData}
                vatPercentage={formData.vatPercentage || 0}
                onChange={handleCompositeFieldChange}
                onTextChange={handleCompositeTextChange}
                onVatPercentageChange={(value) => setFormData((prev) => applyVatPercentageChange(prev, value))}
                formatNumberDisplay={formatNumberDisplay}
                parseNumberInput={parseNumberInput}
                t={t}
              />
            )}

            {/* Tiered Pricing */}
            {formData.pricingType === FeePricingType.TIERED && (
              <div className="space-y-4">
                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('vatPercentage')}</label>
                  <input
                    type="number"
                    value={formData.vatPercentage || 0}
                    onChange={(e) => handleTierVatPercentageChange(parseFloat(e.target.value) || 0)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="100"
                  />
                </div>

                <CompositeFeeGroupSettings
                  values={formData}
                  onNumberChange={handleTierCommonNumberChange}
                  onTextChange={handleTierCommonTextChange}
                  formatNumberDisplay={formatNumberDisplay}
                  parseNumberInput={parseNumberInput}
                  t={t}
                />

                {feeTiers.map((tier, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      {feeTiers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTier(index)}
                          className="text-red-500 hover:text-red-700 text-sm ml-auto"
                        >
                          {t('removeLevel')}
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          {index === 0 ? `${t('usageLevel')} ${t('usageFrom')} 0 ${t('usageTo')}` : `${t('usageLevel')} ${t('usageFrom')} ${feeTiers[index - 1]?.usageTo || 0} ${t('usageTo')}`}
                        </label>
                        <input
                          type="text"
                          value={tier.usageTo || ''}
                          onChange={(e) => handleTierChange(index, 'usageTo', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          placeholder={index === feeTiers.length - 1 ? t('usageToUnlimitedHint') : t('usageLevel')}
                        />
                        {index === feeTiers.length - 1 && (
                          <p className="text-xs text-blue-600 mt-1">{t('usageToLastTierNote')}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">{t('unitLabel')}</label>
                        <input
                          type="text"
                          value={tier.unit || ''}
                          onChange={(e) => handleTierChange(index, 'unit', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          placeholder={t('unitPlaceholder')}
                        />
                      </div>
                    </div>
                    <TierLevelPricingFields
                      tier={tier}
                      tierIndex={index}
                      common={extractTierCommonSettings(formData)}
                      vatPercentage={formData.vatPercentage || 0}
                      onChange={handleTierLevelPricingChange}
                      formatNumberDisplay={formatNumberDisplay}
                      parseNumberInput={parseNumberInput}
                      t={t}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addTier}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  {t('addLevel')}
                </button>
              </div>
            )}

            {/* Quota-Based Pricing */}
            {formData.pricingType === FeePricingType.QUOTA_BASED && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-lg font-medium text-blue-900 mb-4">
                    Tính phí theo định mức
                  </h4>
                  <p className="text-sm text-blue-800 mb-4">
                    Hệ thống sẽ tính phí theo logic: Trong định mức (x) → Ngoài định mức 1 (x/2) → Ngoài định mức 2 (còn lại)
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Định mức sử dụng (x)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.quotaLimit || 0}
                        onChange={(e) => handleInputChange('quotaLimit', parseFloat(e.target.value) || 0)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ví dụ: 10"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Loại tính toán
                      </label>
                      <select
                        value={formData.quotaCalculationType || 'STANDARD'}
                        onChange={(e) => handleInputChange('quotaCalculationType', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="STANDARD">Tiêu chuẩn</option>
                        <option value="WATER">Nước sạch</option>
                        <option value="ELECTRICITY">Điện</option>
                        <option value="GAS">Gas</option>
                      </select>
                    </div>
                  </div>

                  {/* Base Prices Section */}
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <h4 className="text-lg font-medium text-blue-900 mb-4">
                      Đơn giá cơ bản (chưa bao gồm VAT và phí BVMT)
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Đơn giá cơ bản trong định mức
                        </label>
                        <input
                          type="text"
                          value={formatNumberDisplay(formData.baseQuotaPrice || 0)}
                          onChange={(e) => {
                            const rawValue = parseNumberInput(e.target.value);
                            handleInputChange('baseQuotaPrice', parseFloat(rawValue) || 0);
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ví dụ: 5,300"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Đơn giá cơ bản ngoài định mức 1
                        </label>
                        <input
                          type="text"
                          value={formatNumberDisplay(formData.baseOverQuotaPrice1 || 0)}
                          onChange={(e) => {
                            const rawValue = parseNumberInput(e.target.value);
                            handleInputChange('baseOverQuotaPrice1', parseFloat(rawValue) || 0);
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ví dụ: 10,200"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Đơn giá cơ bản ngoài định mức 2
                        </label>
                        <input
                          type="text"
                          value={formatNumberDisplay(formData.baseOverQuotaPrice2 || 0)}
                          onChange={(e) => {
                            const rawValue = parseNumberInput(e.target.value);
                            handleInputChange('baseOverQuotaPrice2', parseFloat(rawValue) || 0);
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ví dụ: 11,400"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Effective Prices Section (Editable) */}
                  <div className="mt-4 p-4 bg-green-50 rounded-lg">
                    <h4 className="text-lg font-medium text-green-900 mb-4">
                      Đơn giá hiệu quả (đã bao gồm VAT và phí BVMT) - Có thể chỉnh sửa
                    </h4>
                    <p className="text-sm text-green-800 mb-4">
                      Giá trị được tính tự động từ đơn giá cơ bản, có thể chỉnh sửa để làm tròn theo VND
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Đơn giá trong định mức (f1)
                        </label>
                        <input
                          type="text"
                          value={formatNumberDisplay(formData.quotaPrice || calculatedQuotaPrice)}
                          onChange={(e) => {
                            const rawValue = parseNumberInput(e.target.value);
                            handleInputChange('quotaPrice', parseFloat(rawValue) || 0);
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Tự động tính từ đơn giá cơ bản"
                        />
                        {formData.baseQuotaPrice && (
                          <div className="text-xs text-gray-500 mt-1">
                            Tính toán: {formatNumberDisplay(calculatedQuotaPrice)}
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Đơn giá ngoài định mức 1 (f2)
                        </label>
                        <input
                          type="text"
                          value={formatNumberDisplay(formData.overQuotaPrice1 || calculatedOverQuotaPrice1)}
                          onChange={(e) => {
                            const rawValue = parseNumberInput(e.target.value);
                            handleInputChange('overQuotaPrice1', parseFloat(rawValue) || 0);
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Tự động tính từ đơn giá cơ bản"
                        />
                        {formData.baseOverQuotaPrice1 && (
                          <div className="text-xs text-gray-500 mt-1">
                            Tính toán: {formatNumberDisplay(calculatedOverQuotaPrice1)}
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Đơn giá ngoài định mức 2 (f3)
                        </label>
                        <input
                          type="text"
                          value={formatNumberDisplay(formData.overQuotaPrice2 || calculatedOverQuotaPrice2)}
                          onChange={(e) => {
                            const rawValue = parseNumberInput(e.target.value);
                            handleInputChange('overQuotaPrice2', parseFloat(rawValue) || 0);
                          }}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Tự động tính từ đơn giá cơ bản"
                        />
                        {formData.baseOverQuotaPrice2 && (
                          <div className="text-xs text-gray-500 mt-1">
                            Tính toán: {formatNumberDisplay(calculatedOverQuotaPrice2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phần trăm hao hụt (b)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.wastagePercentage || 0}
                        onChange={(e) => handleInputChange('wastagePercentage', parseFloat(e.target.value) || 0)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ví dụ: 5.5"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phí bảo vệ môi trường (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.environmentalFee || 0}
                        onChange={(e) => handleInputChange('environmentalFee', parseFloat(e.target.value) || 0)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ví dụ: 10"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {formData.pricingType === FeePricingType.QUOTA_BASED && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('vatPercentage')}
              </label>
              <input
                type="number"
                value={formData.vatPercentage || 0}
                onChange={(e) => handleInputChange('vatPercentage', parseFloat(e.target.value) || 0)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0"
                min="0"
                max="100"
              />
            </div>
            )}

            {formData.pricingType === FeePricingType.SINGLE_TIER && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Đơn vị tính (hiển thị)
                  </label>
                  <input
                    type="text"
                    value={formData.quantityUnit || ''}
                    onChange={(e) => handleInputChange('quantityUnit', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="thẻ, xe, căn hộ, m²..."
                  />
                </div>

                {formData.isMonthlyFixed && formData.autoCloseMonthly ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cách tính số lượng (phí chốt tự động hàng tháng)
                    </label>
                    <select
                      value={formData.quantitySource || FeeQuantitySource.MANUAL}
                      onChange={(e) => handleInputChange('quantitySource', e.target.value as FeeQuantitySource)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={FeeQuantitySource.MANUAL}>Nhập thủ công theo căn hộ</option>
                      <option value={FeeQuantitySource.APARTMENT_AREA}>Theo diện tích căn hộ (m²)</option>
                      <option value={FeeQuantitySource.FIXED_ONE}>Cố định 1 đơn vị / căn hộ</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Dùng cho phí lặp hàng tháng: số lượng lưu theo từng căn hộ (ví dụ số xe, diện tích m²).
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    Phí chốt thủ công — nhân viên nhập số lượng khi tạo từng hóa đơn (ví dụ số thẻ cấp phát).
                  </p>
                )}
              </div>
            )}

            {/* Toggle Options */}
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.isMonthlyFixed || false}
                    onChange={() => handleToggleChange('isMonthlyFixed')}
                    className="sr-only"
                  />
                  <div className={`w-12 h-6 rounded-full ${formData.isMonthlyFixed ? 'bg-green-400' : 'bg-gray-300'} transition-colors`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${formData.isMonthlyFixed ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`}></div>
                  </div>
                </div>
                <span className="text-gray-700">{t('monthlyFixedFee')}</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.autoCloseMonthly || false}
                    onChange={() => handleToggleChange('autoCloseMonthly')}
                    className="sr-only"
                  />
                  <div className={`w-12 h-6 rounded-full ${formData.autoCloseMonthly ? 'bg-red-400' : 'bg-gray-300'} transition-colors`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${formData.autoCloseMonthly ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`}></div>
                  </div>
                </div>
                <span className="text-gray-700">{t('autoCloseMonthly')}</span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kỳ hóa đơn / Billing timing
                </label>
                <select
                  value={formData.billingTiming || 'PREPAID'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      billingTiming: e.target.value as 'PREPAID' | 'POSTPAID',
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="PREPAID">Trả trước (kỳ hiện tại - VD: phí QL, gửi xe)</option>
                  <option value="POSTPAID">Trả sau (kỳ trước - VD: điện, nước)</option>
                </select>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.isActive || false}
                    onChange={() => handleToggleChange('isActive')}
                    className="sr-only"
                  />
                  <div className={`w-12 h-6 rounded-full ${formData.isActive ? 'bg-green-400' : 'bg-red-400'} transition-colors`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${formData.isActive ? 'translate-x-6' : 'translate-x-0.5'} mt-0.5`}></div>
                  </div>
                </div>
                <span className="text-gray-700">{t('statusLabel')} {formData.isActive ? t('active') : t('inactive')}</span>
              </label>
            </div>

            {isVehicleCardIssuanceFee && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.waiveFirstVehicleCardIssuanceFee ?? false}
                    onChange={(e) =>
                      handleInputChange('waiveFirstVehicleCardIssuanceFee', e.target.checked)
                    }
                    className="mt-1 rounded border-gray-300 text-blue-600"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-800">
                      {t('waiveFirstVehicleCardIssuanceFee')}
                    </span>
                    <span className="block text-xs text-gray-600 mt-1">
                      {t('waiveFirstVehicleCardIssuanceFeeHint')}
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('notesLabel')}
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder={t('notesPlaceholder')}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/fee-categories')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {t('close')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? t('saving') : t('save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditFeeCategory; 